import { useToastStore, type ToastItem, type ToastVariant } from '../../stores/toast-store';

/**
 * ToastContainer — render no AppShell (uma única instância).
 *
 * Renderiza stack vertical fixa top-right (mobile usa breakpoint a
 * partir do Brief 6 V2; web v1 cobre apenas desktop). Cada toast tem
 * borda colorida por variant e botão de fechar.
 *
 * Não usa portal explícito — vive dentro da árvore React do AppShell e
 * z-index 50 garante stack acima do conteúdo.
 *
 * Extensão (22/Ago/2026 — Admin Hub T5): quando o toast carrega
 * `actionLabel`+`onAction` (ex.: "Desfazer" da sticky save bar 03c), um
 * segundo botão aparece abaixo da descrição. Aditivo — toast sem esses
 * campos renderiza exatamente como antes.
 */

const VARIANT_CLASSES: Record<ToastVariant, string> = {
  success: 'border-green-500 bg-green-50 text-green-900',
  error: 'border-red-500 bg-red-50 text-red-900',
  info: 'border-blue-500 bg-blue-50 text-blue-900',
};

function ToastCard({ toast }: { toast: ToastItem }): JSX.Element {
  const dismiss = useToastStore((s) => s.dismiss);
  const variantClass = VARIANT_CLASSES[toast.variant];

  return (
    <div
      role={toast.variant === 'error' ? 'alert' : 'status'}
      aria-live={toast.variant === 'error' ? 'assertive' : 'polite'}
      className={`flex items-start gap-3 rounded border-l-4 px-4 py-3 shadow-md min-w-[260px] max-w-[400px] ${variantClass}`}
      data-testid={`toast-${toast.variant}`}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{toast.title}</p>
        {toast.description !== undefined && toast.description.length > 0 && (
          <p className="text-xs mt-1 opacity-80">{toast.description}</p>
        )}
        {toast.actionLabel !== undefined && toast.onAction !== undefined && (
          <button
            type="button"
            onClick={() => {
              toast.onAction?.();
              dismiss(toast.id);
            }}
            className="text-xs font-semibold underline mt-1"
          >
            {toast.actionLabel}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={() => dismiss(toast.id)}
        aria-label="Fechar notificação"
        className="text-xs opacity-60 hover:opacity-100 leading-none px-1"
      >
        ×
      </button>
    </div>
  );
}

export function ToastContainer(): JSX.Element {
  const toasts = useToastStore((s) => s.toasts);
  return (
    <div
      aria-label="Notificações"
      className="fixed top-4 right-4 z-50 flex flex-col gap-2 pointer-events-none"
    >
      {toasts.map((t) => (
        <div key={t.id} className="pointer-events-auto">
          <ToastCard toast={t} />
        </div>
      ))}
    </div>
  );
}
